import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const propertyIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const PropertyLocationMap = ({ 
  property, 
  language = 'en',
  height = '400px'
}) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(true);

  const texts = {
    pt: {
      location: 'Localização',
      approximate: 'Localização aproximada',
      getDirections: 'Obter direções',
      openInMaps: 'Abrir no Google Maps',
      nearDisney: 'Próximo à Disney',
      nearUniversal: 'Próximo à Universal',
      city: 'Cidade',
      neighborhood: 'Bairro'
    },
    en: {
      location: 'Location',
      approximate: 'Approximate location',
      getDirections: 'Get directions',
      openInMaps: 'Open in Google Maps',
      nearDisney: 'Near Disney',
      nearUniversal: 'Near Universal',
      city: 'City',
      neighborhood: 'Neighborhood'
    }
  };

  const t = texts[language];

  useEffect(() => {
    // Try to get coordinates from property or geocode
    if (property.latitude && property.longitude) {
      setCoordinates({
        lat: property.latitude,
        lng: property.longitude
      });
      setLoading(false);
    } else if (property.city && property.state) {
      // Default coordinates for Florida vacation cities
      const cityCoords = {
        'Orlando': { lat: 28.5383, lng: -81.3792 },
        'Kissimmee': { lat: 28.2919, lng: -81.4076 },
        'Davenport': { lat: 28.1614, lng: -81.6017 },
        'Celebration': { lat: 28.3253, lng: -81.5339 },
        'Champions Gate': { lat: 28.2969, lng: -81.6178 },
        'Clermont': { lat: 28.5494, lng: -81.7729 },
        'Winter Garden': { lat: 28.5652, lng: -81.5862 },
        'Lake Buena Vista': { lat: 28.3747, lng: -81.5200 }
      };

      const coords = cityCoords[property.city] || { lat: 28.5383, lng: -81.3792 };
      // Add small random offset for privacy
      setCoordinates({
        lat: coords.lat + (Math.random() - 0.5) * 0.02,
        lng: coords.lng + (Math.random() - 0.5) * 0.02
      });
      setLoading(false);
    } else {
      // Default to Orlando
      setCoordinates({ lat: 28.5383, lng: -81.3792 });
      setLoading(false);
    }
  }, [property]);

  const handleOpenMaps = () => {
    if (coordinates) {
      const url = `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
      window.open(url, '_blank');
    }
  };

  const handleGetDirections = () => {
    if (coordinates) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
      window.open(url, '_blank');
    }
  };

  if (loading || !coordinates) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {t.location}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full bg-gray-200 animate-pulse rounded-lg" style={{ height }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-[#00C2D1]" />
            {t.location}
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGetDirections}
              className="gap-1"
            >
              <Navigation className="w-4 h-4" />
              {t.getDirections}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleOpenMaps}
              className="gap-1"
            >
              <ExternalLink className="w-4 h-4" />
              {t.openInMaps}
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">{t.approximate}</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="relative" style={{ height }}>
          <MapContainer 
            center={[coordinates.lat, coordinates.lng]} 
            zoom={14} 
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
            
            {/* Approximate location circle */}
            <Circle 
              center={[coordinates.lat, coordinates.lng]}
              radius={500}
              pathOptions={{ 
                color: '#00C2D1', 
                fillColor: '#00C2D1', 
                fillOpacity: 0.1,
                weight: 2
              }}
            />
            
            {/* Property marker */}
            <Marker 
              position={[coordinates.lat, coordinates.lng]} 
              icon={propertyIcon}
            >
              <Popup>
                <div className="text-center p-2">
                  <p className="font-semibold">{property.title}</p>
                  <p className="text-sm text-gray-600">{property.city}, {property.state}</p>
                  {property.distance_to_disney_miles && (
                    <p className="text-xs text-gray-500 mt-1">
                      🏰 {property.distance_to_disney_miles} mi to Disney
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
        
        {/* Location details below map */}
        <div className="p-4 bg-slate-50 border-t">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              <span><strong>{t.city}:</strong> {property.city}, {property.state}</span>
            </div>
            {property.neighborhood && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span><strong>{t.neighborhood}:</strong> {property.neighborhood}</span>
              </div>
            )}
            {property.distance_to_disney_miles && (
              <div className="flex items-center gap-2">
                <span>🏰</span>
                <span><strong>{t.nearDisney}:</strong> {property.distance_to_disney_miles} mi</span>
              </div>
            )}
            {property.distance_to_universal_miles && (
              <div className="flex items-center gap-2">
                <span>🎢</span>
                <span><strong>{t.nearUniversal}:</strong> {property.distance_to_universal_miles} mi</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyLocationMap;
